import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  MdLocationOn,
  MdSearchOff,
  MdStorefront,
  MdSearch,
} from "react-icons/md";

import styles from "./Store.module.scss";
import { useStore } from "./useStore";
import { storeRoute } from "../../common/utils/constants";
import { PageHeader } from "../../common/components/pageHeader/PageHeader";
import { Search } from "../../common/components/search/Search";
import { useSearch } from "../../common/components/search/useSearch";
import { EmptyState } from "../../common/components/emptyState/EmptyState";
import { getClusterImageUrl } from "../../common/utils/functions/getImageUri";

export const Store: React.FC = () => {
  const { query, setQuery, clearSearch, isDebouncing } = useSearch();
  const { items, cluster, count, ready, isFetching } = useStore();

  if (!ready) return null;

  const title = cluster ? `Точки — ${cluster.name}` : "Точки";
  const subtitle = `${count} ${count === 1 ? "точка" : count < 5 ? "точки" : "точек"}`;

  const isBusy = isFetching || isDebouncing;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`${title} — logmall`}</title>
        <meta
          name="description"
          content={
            cluster
              ? `Точки компании ${cluster.name}`
              : "Точки компаний на logmall"
          }
        />
      </Helmet>

      <PageHeader
        title={title}
        subtitle={subtitle}
        logo={
          cluster ? `https://${cluster.domain}/icons/logo512.png` : undefined
        }
      />

      <div className={styles.SearchWrap}>
        <Search
          value={query}
          onChange={setQuery}
          clear={clearSearch}
          placeHolder="Поиск по названию или адресу..."
        />
      </div>

      {items.length === 0 && !isBusy ? (
        query.trim() ? (
          <EmptyState
            icon={MdSearchOff}
            title="Ничего не найдено"
            description={`По запросу «${query}» нет ни одной точки. Попробуйте изменить запрос.`}
          />
        ) : (
          <EmptyState
            icon={MdSearch}
            title="Введите запрос"
            description="Начните вводить название или адрес точки, чтобы найти нужную."
          />
        )
      ) : items.length === 0 && isBusy ? (
        <div style={{ minHeight: 400 }} />
      ) : (
        <div className={styles.List}>
          {items.map(({ store: s, cluster: storeCluster }) => {
            const hasCoords = s.address?.latitude && s.address?.longitude;
            const mapQuery = hasCoords
              ? `${s.address!.latitude},${s.address!.longitude}`
              : [s.address?.value, s.city?.value].filter(Boolean).join(", ");

            return (
              <Link
                key={s.id}
                to={storeRoute(s.id, s.clusterId)}
                className={styles.Row}
              >
                {s.files?.[0] && storeCluster && (
                  <img
                    src={getClusterImageUrl(storeCluster, s.files[0])}
                    alt={s.name}
                    className={styles.RowImage}
                    loading="lazy"
                  />
                )}

                <div className={styles.RowContent}>
                  <span className={styles.RowBgName} aria-hidden="true">
                    {s.name}
                  </span>

                  <div className={styles.RowMeta}>
                    <span className={styles.RowName}>{s.name}</span>
                    {storeCluster && (
                      <span className={styles.RowCluster}>
                        {storeCluster.name}
                      </span>
                    )}
                    {s.address?.value && (
                      <span className={styles.RowAddress}>
                        {s.address.value}
                      </span>
                    )}
                  </div>
                </div>

                {s.vacanciesCount && s.vacanciesCount > 0 ? (
                  <span className={styles.RowCount} aria-label="Вакансий">
                    {s.vacanciesCount}
                  </span>
                ) : (
                  <span className={styles.RowCountEmpty} aria-hidden="true" />
                )}

                {mapQuery && (
                  <span className={styles.RowMap}>
                    <MdLocationOn size={28} />
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
